<template>
  <asc-page-loading-indicator
    :loading="isDataLoading && apiPageFailure.length === 0"
  >
    <div class="asc-home-container">
      <v-container fluid>
        <asc-metrics-template
          :dynamic-counter-data="dynamicCounterData"
          :issue-count-data="getStatusCount"
          :internal-user="isInternalView"
        ></asc-metrics-template>

        <asc-maintenance-list-template
          v-if="
            hasResourcePermission({
              resource: resources.MAINTENANCE_REC,
              permissionLevel: permissionLevelConsts.READ,
            })
          "
          :title="getGridTitle"
          tooltip="10 Latest Items Listed"
          :values="homePageMaintenanceIssues"
          :internal-user="isInternalView"
          :grid-columns="getGridColumns"
          :grid-filters="false"
          :maintenance-export-table-grid-permission="
            maintenanceExportTableGridPermission
          "
          :maintenance-grid-permission="maintenanceGridPermission"
          navigation-button-visible
        ></asc-maintenance-list-template>
      </v-container>
    </div>
  </asc-page-loading-indicator>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import {
  AscMaintenanceListTemplate,
  AscMetricsTemplate,
} from '@/components/templates'
import {
  GLOBAL_FILTER_CONSTS as GF,
  MAINTENANCE_ISSUES_CONSTS as MIC,
  USER_CONSTS as UC,
  LOOKUP_CONSTS as LC,
} from '@/stores/consts'
import { PERMISSION_LEVEL_CONSTS, RESOURCES } from '@/utilities/appConsts'
import { getMaintenanceGridColumns } from '@/utilities/maintenance'

export default {
  name: 'AscHomeContainer',

  components: {
    AscPageLoadingIndicator,
    AscMaintenanceListTemplate,
    AscMetricsTemplate,
  },

  data() {
    return {
      isMaintenanceDataLoading: false,
      isSummaryDataLoading: false,
      permissionLevelConsts: PERMISSION_LEVEL_CONSTS,
      resources: RESOURCES,
    }
  },

  computed: {
    ...mapState({ apiPageFailure: 'apiPageFailure' }),
    ...mapState(GF.NAMESPACE, ['globalFilter']),
    ...mapGetters(UC.NAMESPACE, [
      'mappedUserAirlines',
      'isIssueReader',
      'isIssueInvestigator',
      'maintenanceExportTableGridPermission',
      'maintenanceGridPermission',
    ]),
    ...mapGetters(LC.NAMESPACE, ['mappedUserMasterModels']),
    ...mapState(MIC.NAMESPACE, [
      'statusSummary',
      'homePageMaintenanceIssues',
      'maintenanceStatuses',
    ]),

    isInternalView() {
      return this.isIssueReader || this.isIssueInvestigator
    },

    isDataLoading() {
      return this.isMaintenanceDataLoading || this.isSummaryDataLoading
    },

    getGridTitle() {
      return 'Latest Maintenance & Alerts'
    },

    getGridColumns() {
      return getMaintenanceGridColumns(
        this.isInternalView,
        this.mappedUserAirlines,
        this.mappedUserMasterModels,
      )
    },

    getStatusCount() {
      if (
        this.statusSummary &&
        this.maintenanceStatuses &&
        this.maintenanceStatuses.length > 0
      ) {
        const statusList = []

        for (const status in this.statusSummary) {
          const foundStatuses = this.maintenanceStatuses.find(
            item => item.name === status,
          )

          statusList.push({
            text: status,
            count: this.statusSummary[status],
            sortOrder: foundStatuses ? foundStatuses.sortOrder : -1,
          })
        }

        return {
          values: statusList
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .splice(0, 15),
          title: 'Open Mx Rec Count By Status',
          seriesName: 'Issues',
          categoryName: 'Status',
        }
      }
      return {}
    },

    dynamicCounterData() {
      return [
        {
          label: 'New Mx Recs',
          link: '/maintenance?status=New Mx Rec',
          value: this.statusSummary ? this.statusSummary['New Mx Rec'] : null,
        },
        {
          label: 'Maintenance Scheduled',
          link: '/maintenance?status=Maintenance Scheduled',
          value: this.statusSummary
            ? this.statusSummary['Maintenance Scheduled']
            : null,
        },
      ]
    },
  },

  watch: {
    globalFilter: {
      deep: true,
      handler() {
        this.getMaintenanceData()
      },
    },
  },

  created() {
    this.getMaintenanceData()
  },

  methods: {
    ...mapActions(MIC.NAMESPACE, [
      MIC.GET_HOME_PAGE_MAINTENANCE_ISSUES,
      MIC.GET_STATUS_SUMMARY,
      MIC.GET_MAINTENANCE_STATUSES,
    ]),

    async getMaintenanceData() {
      this.isMaintenanceDataLoading = true
      if (Object.keys(this.globalFilter).length) {
        await Promise.all([
          this[MIC.GET_MAINTENANCE_STATUSES](),
          this[MIC.GET_HOME_PAGE_MAINTENANCE_ISSUES](),
          this[MIC.GET_STATUS_SUMMARY](),
        ])
      }
      this.isMaintenanceDataLoading = false
    },
  },
}
</script>

<style lang="scss">
.asc-home-container {
  background-color: $gray-100;

  & .container {
    padding: 16px 16px 0 16px;
  }
}
</style>
